<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12">
        <b-card>
          <div v-if="loading" class="text-center py-5">
            <b-spinner></b-spinner>
            <p>Carregando...</p>
          </div>

          <div v-else-if="questionnaire">
            <b-alert show variant="info">
              <h5 class="alert-heading">
                {{ questionnaire.title }}
              </h5>
              <p>{{ questionnaire.description }}</p>
            </b-alert>

            <!-- Area Selection -->
            <b-form @submit.prevent="sendInvitations">
              <b-form-group
                label="Selecionar Setores (Áreas)"
                description="Selecione os setores que receberão o questionário"
              >
                <div v-if="loadingAreas" class="text-center py-3">
                  <b-spinner small></b-spinner> Carregando setores...
                </div>
                <div v-else-if="availableAreas.length === 0">
                  <b-alert show variant="warning">
                    Nenhum setor disponível. Certifique-se de que há
                    funcionários cadastrados com áreas definidas.
                  </b-alert>
                </div>
                <div v-else>
                  <b-form-checkbox
                    v-model="selectAll"
                    @change="toggleSelectAll"
                    class="mb-2"
                  >
                    <strong>Selecionar Todos</strong>
                  </b-form-checkbox>
                  <b-form-checkbox-group
                    v-model="selectedAreas"
                    :options="areaOptions"
                    stacked
                  ></b-form-checkbox-group>
                </div>
              </b-form-group>

              <hr />

              <!-- Manual Recipients -->
              <b-form-group
                label="Adicionar Destinatários Manuais"
                description="Adicione nomes e e-mails de pessoas que não estão cadastradas como colaboradores para receberem o link"
              >
                <b-row>
                  <b-col md="5">
                    <b-form-input
                      v-model="newRecipient.name"
                      placeholder="Nome completo"
                      size="sm"
                    ></b-form-input>
                  </b-col>
                  <b-col md="5">
                    <b-form-input
                      v-model="newRecipient.email"
                      type="email"
                      placeholder="email@exemplo.com"
                      size="sm"
                    ></b-form-input>
                  </b-col>
                  <b-col md="2">
                    <b-button
                      block
                      variant="outline-primary"
                      size="sm"
                      @click="addManualRecipient"
                      :disabled="
                        !newRecipient.name ||
                        !newRecipient.email ||
                        addingRecipient
                      "
                    >
                      <b-spinner v-if="addingRecipient" small></b-spinner>
                      <i v-else class="fa fa-plus"></i> Adicionar
                    </b-button>
                  </b-col>
                </b-row>

                <div v-if="manualRecipients.length > 0" class="mt-3">
                  <b-list-group>
                    <b-list-group-item
                      v-for="(recipient, index) in manualRecipients"
                      :key="index"
                      class="d-flex justify-content-between align-items-center py-2"
                    >
                      <span>
                        <strong>{{ recipient.name }}</strong>
                        <span class="text-muted ml-2"
                          >&lt;{{ recipient.email }}&gt;</span
                        >
                      </span>
                      <b-button
                        variant="link"
                        class="text-danger p-0"
                        @click="removeManualRecipient(index)"
                      >
                        <i class="fa fa-times"></i>
                      </b-button>
                    </b-list-group-item>
                  </b-list-group>
                </div>
              </b-form-group>

              <b-alert
                :show="selectedAreas.length > 0 || manualRecipients.length > 0"
                variant="success"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <span v-if="selectedAreas.length > 0">
                      <strong>{{ newInternalRecipientsCount }}</strong> novo(s)
                      convite(s) para os setores
                    </span>
                    <span
                      v-if="
                        selectedAreas.length > 0 && manualRecipients.length > 0
                      "
                    >
                      e
                    </span>
                    <span v-if="manualRecipients.length > 0">
                      <strong>{{ manualRecipients.length }}</strong>
                      destinatário(s) manual(is)
                    </span>
                  </div>
                  <div class="text-right">
                    <span class="h5 mb-0"
                      >Total:
                      {{
                        newInternalRecipientsCount + manualRecipients.length
                      }}</span
                    >
                  </div>
                </div>
              </b-alert>

              <hr />

              <b-button
                type="submit"
                variant="primary"
                :disabled="
                  sending ||
                  (newInternalRecipientsCount === 0 &&
                    manualRecipients.length === 0)
                "
              >
                <b-spinner v-if="sending" small></b-spinner>
                <i v-else class="fa fa-paper-plane"></i>
                {{ sending ? "Enviando..." : "Enviar Convites por Email" }}
              </b-button>
            </b-form>

            <!-- Result -->
            <div v-if="result" class="mt-4">
              <b-alert
                :variant="result.errors.length > 0 ? 'warning' : 'success'"
                show
              >
                <h5 class="alert-heading">Resultado do Envio</h5>
                <hr />
                <p>
                  <strong>Enviados com sucesso:</strong> {{ result.sent }}<br />
                  <strong>Falhas:</strong> {{ result.failed }}
                </p>
                <div v-if="result.errors.length > 0" class="mt-2">
                  <small>
                    <strong>Erros:</strong>
                    <ul>
                      <li v-for="(err, idx) in result.errors" :key="idx">
                        {{ err.email }}: {{ err.error }}
                      </li>
                    </ul>
                  </small>
                </div>
              </b-alert>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  getQuestionnaire,
  getAreas,
  distributeQuestionnaire,
  getPotentialRecipients,
  checkEmailParticipation,
} from "@/api/nr1";

export default {
  name: "NR1Distribution",
  props: {
    questionnaireId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loadingAreas: false,
      sending: false,
      questionnaire: null,
      availableAreas: [],
      selectedAreas: [],
      selectAll: false,
      internalRecipients: [],
      loadingRecipients: false,
      newRecipient: { name: "", email: "" },
      manualRecipients: [],
      addingRecipient: false,
      result: null,
    };
  },
  computed: {
    areaOptions() {
      return this.availableAreas.map((area) => ({
        text: area,
        value: area,
      }));
    },
    newInternalRecipientsCount() {
      return this.internalRecipients.filter((r) => !r.alreadyParticipated)
        .length;
    },
  },
  watch: {
    selectedAreas: {
      handler(newAreas) {
        if (newAreas.length > 0) {
          this.loadRecipients();
        } else {
          this.internalRecipients = [];
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        await Promise.all([this.loadQuestionnaire(), this.loadAreas()]);
      } finally {
        this.loading = false;
      }
    },
    async loadQuestionnaire() {
      try {
        const response = await getQuestionnaire(this.questionnaireId);
        this.questionnaire = response.data;
        if (this.questionnaire.targetAreas) {
          this.selectedAreas = [...this.questionnaire.targetAreas];
        }
      } catch (error) {
        console.error("Error loading questionnaire:", error);
        this.$bvToast.toast("Erro ao carregar questionário", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        this.$emit("cancel");
      }
    },
    async loadAreas() {
      this.loadingAreas = true;
      try {
        const response = await getAreas();
        this.availableAreas = response.data;
      } catch (error) {
        console.error("Error loading areas:", error);
        this.$bvToast.toast("Erro ao carregar setores", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.loadingAreas = false;
      }
    },
    async loadRecipients() {
      if (this.selectedAreas.length === 0) return;

      this.loadingRecipients = true;
      try {
        const response = await getPotentialRecipients(
          this.questionnaireId,
          this.selectedAreas
        );
        this.internalRecipients = response.data;
      } catch (error) {
        console.error("Error loading recipients:", error);
      } finally {
        this.loadingRecipients = false;
      }
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedAreas = [...this.availableAreas];
      } else {
        this.selectedAreas = [];
      }
    },
    async sendInvitations() {
      if (
        this.selectedAreas.length === 0 &&
        this.manualRecipients.length === 0
      ) {
        this.$bvToast.toast(
          "Selecione pelo menos um setor ou adicione um destinatário manual",
          {
            title: "Atenção",
            toastClass: "custom-toast custom-toast-warning",
            variant: "light",
            solid: true,
            autoHideDelay: 8000,
            noCloseButton: false,
          }
        );
        return;
      }

      this.sending = true;
      this.result = null;

      try {
        const response = await distributeQuestionnaire(this.questionnaireId, {
          targetAreas: this.selectedAreas,
          customParticipants: this.manualRecipients,
        });

        this.result = response.data;

        this.$bvToast.toast(
          `Convites enviados para ${this.result.sent} colaborador(es)`,
          {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          }
        );
      } catch (error) {
        console.error("Error sending invitations:", error);
        this.$bvToast.toast(
          error.response?.data?.message || "Erro ao enviar convites",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      } finally {
        this.sending = false;
      }
    },
    async addManualRecipient() {
      if (this.newRecipient.name && this.newRecipient.email) {
        const email = this.newRecipient.email.toLowerCase().trim();

        // Simple email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          this.$bvToast.toast(
            "Por favor, informe um endereço de e-mail válido",
            {
              title: "E-mail inválido",
              toastClass: "custom-toast custom-toast-warning",
              variant: "light",
              solid: true,
              autoHideDelay: 8000,
              noCloseButton: false,
            }
          );
          return;
        }

        // Check if already in the local list
        if (
          this.manualRecipients.some((r) => r.email.toLowerCase() === email)
        ) {
          this.$bvToast.toast(
            "Este e-mail já está na lista de destinatários manuais",
            {
              title: "E-mail duplicado",
              toastClass: "custom-toast custom-toast-warning",
              variant: "light",
              solid: true,
              autoHideDelay: 8000,
              noCloseButton: false,
            }
          );
          return;
        }

        // Check if already in the sectors list (if sectors are selected)
        if (
          this.internalRecipients.some((r) => r.email.toLowerCase() === email)
        ) {
          this.$bvToast.toast(
            "O colaborador com este e-mail já está incluído nos setores selecionados.",
            {
              title: "Atenção",
              toastClass: "custom-toast custom-toast-warning",
              variant: "light",
              solid: true,
              autoHideDelay: 8000,
              noCloseButton: false,
            }
          );
          return;
        }

        this.addingRecipient = true;
        try {
          const check = await checkEmailParticipation(
            this.questionnaireId,
            email
          );
          if (check.data.exists) {
            this.$bvToast.toast(
              check.data.message ||
                `O e-mail ${email} já recebeu um convite para este questionário.`,
              {
                title: "Já enviado",
                toastClass: "custom-toast custom-toast-warning",
                variant: "light",
                solid: true,
                autoHideDelay: 8000,
                noCloseButton: false,
              }
            );
            this.addingRecipient = false;
            return;
          }
        } catch (error) {
          console.error("Error checking email participation:", error);
          // Proceed anyway if check fails, but log it
        } finally {
          this.addingRecipient = false;
        }

        this.manualRecipients.push({ ...this.newRecipient, email });
        this.newRecipient = { name: "", email: "" };
        this.$bvToast.toast("Destinatário adicionado à lista", {
          title: "Adicionado",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } else {
        this.$bvToast.toast("Preencha nome e e-mail para adicionar", {
          title: "Campos obrigatórios",
          toastClass: "custom-toast custom-toast-warning",
          variant: "light",
          solid: true,
          autoHideDelay: 8000,
          noCloseButton: false,
        });
      }
    },
    removeManualRecipient(index) {
      this.manualRecipients.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.card {
  margin-bottom: 1rem;
}
</style>
